/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { eventsAPI, viewsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import EmailNotifyToggle from '../components/EmailNotifyToggle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Calendar, MapPin, Users, Plus, FileDown, ArrowLeft, Loader2, Search, X,
  Clock, CheckCircle, XCircle, AlertCircle, Trash2, Send, Edit,
  ClipboardList, CalendarDays, UserCheck, FileText, Ticket, ArrowRight,
  Download,
} from 'lucide-react';

const STATUS_MAP = {
  aktif: { label: 'Aktif', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  kapali: { label: 'Kapalı', color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  iptal: { label: 'İptal', color: 'bg-red-50 text-red-600 border-red-200', dot: 'bg-red-500' },
};

const APP_STATUS = {
  beklemede: { label: 'Beklemede', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertCircle, iconColor: 'text-amber-500' },
  onaylandi: { label: 'Onaylandı', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle, iconColor: 'text-emerald-500' },
  reddedildi: { label: 'Reddedildi', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle, iconColor: 'text-red-500' },
};

const FIELD_TYPES = [
  { value: 'text', label: 'Kısa Cevap' },
  { value: 'textarea', label: 'Uzun Cevap' },
  { value: 'select', label: 'Çoktan Seçmeli' },
  { value: 'radio', label: 'Tek Seçim' },
  { value: 'checkbox', label: 'Evet / Hayır' },
];


// =================== MAIN EVENTS PAGE (TABBED) ===================
const EventsListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('Etkinlikler');
  const [activeTab, setActiveTab] = useState('etkinlikler');
  const [events, setEvents] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appsLoading, setAppsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQ, setSearchQ] = useState('');
  const [statusF, setStatusF] = useState('');
  const isAdmin = ['admin', 'genel_sekreter', 'meclis_baskani', 'kent_konseyi_baskani'].includes(user?.role);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await eventsAPI.getAll({ search: searchQ || undefined, status: statusF || undefined, limit: 50 });
      setEvents(r.data.events); setTotal(r.data.total);
    } catch { toast.error('Etkinlikler yüklenemedi'); }
    finally { setLoading(false); }
  }, [searchQ, statusF]);

  const loadMyApps = useCallback(async () => {
    setAppsLoading(true);
    try { const r = await eventsAPI.getMyApplications(); setMyApps(r.data); }
    catch {}
    finally { setAppsLoading(false); }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { if (activeTab === 'basvurularim') loadMyApps(); }, [activeTab, loadMyApps]);

  const appCounts = {
    beklemede: myApps.filter(a => a.status === 'beklemede').length,
    onaylandi: myApps.filter(a => a.status === 'onaylandi').length,
    reddedildi: myApps.filter(a => a.status === 'reddedildi').length,
  };

  return (
    <div className="space-y-6" data-testid="events-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Etkinlikler</h1>
          <p className="text-sm text-slate-500 mt-0.5">Etkinlikleri görüntüleyin ve başvuru yapın</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { const a=document.createElement('a'); a.href=`${process.env.REACT_APP_BACKEND_URL}/api/events/export/excel`; a.click(); }} data-testid="export-events-btn" className="rounded-xl">
              <FileDown className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button onClick={() => navigate('/panel/etkinlikler/yeni')} className="gap-2 bg-blue-600 hover:bg-blue-700 rounded-xl h-10 px-5 shadow-sm" data-testid="create-event-btn">
              <Plus className="w-4 h-4" /> Yeni Etkinlik
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl w-fit" data-testid="events-tabs">
        <button onClick={() => setActiveTab('etkinlikler')}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'etkinlikler' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-etkinlikler">
          <CalendarDays className="w-4 h-4" /> Etkinlikler
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === 'etkinlikler' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'}`}>{total}</span>
        </button>
        <button onClick={() => setActiveTab('basvurularim')}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'basvurularim' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-basvurularim">
          <Ticket className="w-4 h-4" /> Başvurularım
          {myApps.length > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === 'basvurularim' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'}`}>{myApps.length}</span>}
        </button>
      </div>

      {/* === ETKINLIKLER TAB === */}
      {activeTab === 'etkinlikler' && (
        <>
          {/* Search + Filters */}
          <div className="flex items-center gap-3">
            <form onSubmit={(e) => { e.preventDefault(); loadEvents(); }} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Etkinlik ara..." className="pl-9 h-10 rounded-xl border-slate-200" data-testid="event-search" />
              {searchQ && <button type="button" onClick={() => { setSearchQ(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
            </form>
            <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-lg">
              {[{ v: '', l: 'Tümü' }, { v: 'aktif', l: 'Aktif' }, { v: 'kapali', l: 'Kapalı' }].map(f => (
                <button key={f.v} onClick={() => setStatusF(f.v)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${statusF === f.v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid={`filter-${f.v || 'all'}`}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>

          {/* Event Grid */}
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>
          ) : events.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <CalendarDays className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-lg font-medium text-slate-700 mb-1">Henüz etkinlik yok</p>
              <p className="text-sm text-slate-400">Yeni bir etkinlik oluşturarak başlayın</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {events.map(ev => {
                const st = STATUS_MAP[ev.status] || STATUS_MAP.aktif;
                const progress = ev.quota ? Math.min((ev.application_count / ev.quota) * 100, 100) : 0;
                return (
                  <div key={ev.id}
                    onClick={() => navigate(`/panel/etkinlikler/${ev.id}`)}
                    className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                    data-testid={`event-card-${ev.id}`}>
                    {/* Color Bar */}
                    <div className={`h-1 ${ev.status === 'aktif' ? 'bg-gradient-to-r from-blue-500 to-emerald-500' : 'bg-slate-200'}`} />
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <Badge className={`${st.color} border text-[11px] font-medium`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot} mr-1.5`} />
                          {st.label}
                        </Badge>
                        {ev.meclis_name && <span className="text-[11px] text-slate-400 truncate max-w-[120px]">{ev.meclis_name}</span>}
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1.5 line-clamp-2 group-hover:text-blue-700 transition-colors">{ev.title}</h3>
                      {ev.description && <p className="text-sm text-slate-500 line-clamp-2 mb-4 leading-relaxed">{ev.description}</p>}

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2.5 text-sm">
                          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Calendar className="w-3.5 h-3.5 text-blue-600" />
                          </div>
                          <span className="text-slate-700">{formatDate(ev.date)}</span>
                          {ev.time && <span className="text-slate-400 text-xs">{ev.time}</span>}
                        </div>
                        {ev.location && (
                          <div className="flex items-center gap-2.5 text-sm">
                            <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                              <MapPin className="w-3.5 h-3.5 text-purple-600" />
                            </div>
                            <span className="text-slate-700 truncate">{ev.location}</span>
                          </div>
                        )}
                      </div>

                      {/* Quota Bar */}
                      {ev.quota && (
                        <div>
                          <div className="flex items-center justify-between text-xs mb-1.5">
                            <span className="text-slate-400">Kontenjan</span>
                            <span className={`font-semibold ${progress >= 100 ? 'text-red-600' : 'text-slate-700'}`}>{ev.application_count} / {ev.quota}</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${progress >= 100 ? 'bg-red-500' : progress >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
                              style={{ width: `${progress}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* === BASVURULARIM TAB === */}
      {activeTab === 'basvurularim' && (
        <>
          {appsLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>
          ) : myApps.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <Ticket className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-lg font-medium text-slate-700 mb-1">Henüz başvurunuz yok</p>
              <p className="text-sm text-slate-400 mb-6">Etkinlikler sekmesinden bir etkinliğe başvurabilirsiniz</p>
              <Button variant="outline" onClick={() => setActiveTab('etkinlikler')} className="gap-2 rounded-xl">
                <CalendarDays className="w-4 h-4" /> Etkinliklere Göz At
              </Button>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { key: 'beklemede', label: 'Beklemede', count: appCounts.beklemede, bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertCircle, iconColor: 'text-amber-500' },
                  { key: 'onaylandi', label: 'Onaylandı', count: appCounts.onaylandi, bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle, iconColor: 'text-emerald-500' },
                  { key: 'reddedildi', label: 'Reddedildi', count: appCounts.reddedildi, bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, iconColor: 'text-red-500' },
                ].map(s => (
                  <div key={s.key} className={`${s.bg} border ${s.border} rounded-xl p-4 flex items-center gap-3`} data-testid={`app-summary-${s.key}`}>
                    <s.icon className={`w-5 h-5 ${s.iconColor}`} />
                    <div>
                      <p className="text-2xl font-semibold text-slate-900">{s.count}</p>
                      <p className="text-xs text-slate-500">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Application List */}
              <div className="space-y-3">
                {myApps.map(app => {
                  const as = APP_STATUS[app.status] || APP_STATUS.beklemede;
                  return (
                    <div key={app.id}
                      onClick={() => navigate(`/panel/etkinlikler/${app.event_id}`)}
                      className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:shadow-md hover:border-slate-300 transition-all cursor-pointer group"
                      data-testid={`my-app-${app.id}`}>
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${app.status === 'onaylandi' ? 'bg-emerald-100' : app.status === 'reddedildi' ? 'bg-red-100' : 'bg-amber-100'}`}>
                        {React.createElement(as.icon, { className: `w-5 h-5 ${as.iconColor}` })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate group-hover:text-blue-700 transition-colors">{app.event_title || 'Etkinlik'}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                          {app.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(app.event_date)}</span>}
                          {app.event_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{app.event_location}</span>}
                          <span>Başvuru: {formatDate(app.created_at)}</span>
                        </div>
                      </div>
                      <Badge className={`${as.color} border text-xs font-medium`}>{as.label}</Badge>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};


// =================== NEW EVENT ===================
const NewEventPage = () => {
  const navigate = useNavigate();
  usePageTitle('Yeni Etkinlik');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', date: '', time: '', end_date: '', location: '', quota: '',
    meclis_id: '', meclis_name: '', custom_fields: [], terms: '',
  });
  const [sendEmail, setSendEmail] = useState(false);
  const [emailAudiences, setEmailAudiences] = useState([]);

  const addField = () => {
    const newField = { id: `f_${Date.now()}`, label: '', type: 'text', options: [], required: false };
    setForm(p => ({ ...p, custom_fields: [...p.custom_fields, newField] }));
  };
  const updateField = (idx, key, val) => {
    setForm(p => {
      const fields = [...p.custom_fields];
      fields[idx] = { ...fields[idx], [key]: val };
      return { ...p, custom_fields: fields };
    });
  };
  const removeField = (idx) => setForm(p => ({ ...p, custom_fields: p.custom_fields.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) { toast.error('Başlık ve tarih zorunludur'); return; }
    setLoading(true);
    try {
      const payload = { ...form, quota: form.quota ? parseInt(form.quota) : null,
        send_notification_email: sendEmail,
        email_target_audiences: sendEmail ? (emailAudiences.length > 0 ? emailAudiences : ['tum_kullanicilar']) : null,
      };
      await eventsAPI.create(payload);
      toast.success('Etkinlik oluşturuldu');
      navigate('/panel/etkinlikler');
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="new-event-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/panel/etkinlikler')} className="rounded-xl"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Yeni Etkinlik</h1>
          <p className="text-sm text-slate-500">Etkinlik bilgilerini doldurun ve başvuru formu oluşturun</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="shadow-sm rounded-2xl border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><CalendarDays className="w-4 h-4 text-blue-600" /> Temel Bilgiler</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Başlık *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Etkinlik başlığı" className="rounded-xl" data-testid="event-title" /></div>
            <div className="space-y-2"><Label>Açıklama</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Etkinlik hakkında detaylı bilgi..." className="rounded-xl" data-testid="event-desc" /></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Tarih *</Label>
                <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="rounded-xl" data-testid="event-date" /></div>
              <div className="space-y-2"><Label>Saat</Label>
                <Input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} className="rounded-xl" data-testid="event-time" /></div>
              <div className="space-y-2"><Label>Bitiş Tarihi</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} className="rounded-xl" data-testid="event-end-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Konum</Label>
                <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Etkinlik yeri" className="rounded-xl" data-testid="event-location" /></div>
              <div className="space-y-2"><Label>Kontenjan</Label>
                <Input type="number" value={form.quota} onChange={e => setForm(p => ({ ...p, quota: e.target.value }))} placeholder="Sınırsız" className="rounded-xl" data-testid="event-quota" /></div>
            </div>
            <div className="space-y-2"><Label>Düzenleyen Meclis</Label>
              <Input value={form.meclis_name} onChange={e => setForm(p => ({ ...p, meclis_name: e.target.value }))} placeholder="Meclis adı (opsiyonel)" className="rounded-xl" data-testid="event-meclis" /></div>
            <div className="space-y-2"><Label>Etkinlik Şartları</Label>
              <Textarea rows={5} value={form.terms} onChange={e => setForm(p => ({ ...p, terms: e.target.value }))} placeholder="Başvuru sahiplerinin kabul etmesi gereken etkinlik şartlarını yazın... (opsiyonel)" className="rounded-xl" data-testid="event-terms" />
              <p className="text-xs text-slate-400">Şart girilirse başvuru sırasında kullanıcıdan onay kutusu ile kabul etmesi istenir.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm rounded-2xl border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4 text-purple-600" /> Başvuru Formu Soruları</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addField} className="rounded-lg gap-1.5" data-testid="add-field-btn"><Plus className="w-3.5 h-3.5" /> Soru Ekle</Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Başvuru sırasında kullanıcılardan toplanacak ek bilgiler</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {form.custom_fields.length === 0 ? (
              <div className="py-10 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 mb-1">Henüz soru eklenmedi</p>
                <p className="text-xs text-slate-400">Başvuru formuna özel sorular ekleyebilirsiniz</p>
              </div>
            ) : form.custom_fields.map((field, idx) => (
              <div key={field.id} className="p-4 rounded-xl border border-slate-200 bg-white space-y-3 relative group" data-testid={`field-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">Soru {idx + 1}</span>
                  <button type="button" onClick={() => removeField(idx)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2 space-y-1"><Label className="text-xs">Soru Metni</Label>
                    <Input value={field.label} onChange={e => updateField(idx, 'label', e.target.value)} placeholder="Sorunuzu yazın..." className="h-9 text-sm rounded-lg" /></div>
                  <div className="space-y-1"><Label className="text-xs">Alan Tipi</Label>
                    <Select value={field.type} onValueChange={v => updateField(idx, 'type', v)}>
                      <SelectTrigger className="h-9 text-sm rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>{FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                {(field.type === 'select' || field.type === 'radio') && (
                  <div className="space-y-1"><Label className="text-xs">Seçenekler (virgülle ayırın)</Label>
                    <Input value={(field.options || []).join(', ')} onChange={e => updateField(idx, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      placeholder="Seçenek 1, Seçenek 2, Seçenek 3" className="h-9 text-sm rounded-lg" /></div>
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.required} onChange={e => updateField(idx, 'required', e.target.checked)} className="rounded border-slate-300 text-blue-600" />
                  <span className="text-xs text-slate-600">Zorunlu alan</span>
                </label>
              </div>
            ))}
          </CardContent>
        </Card>

        <EmailNotifyToggle
          sendEmail={sendEmail} setSendEmail={setSendEmail}
          emailAudiences={emailAudiences} setEmailAudiences={setEmailAudiences}
          defaultAudiences={['tum_kullanicilar']}
        />

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/panel/etkinlikler')} className="rounded-xl">İptal</Button>
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-xl shadow-sm" disabled={loading} data-testid="event-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Etkinlik Oluştur
          </Button>
        </div>
      </form>
    </div>
  );
};


// =================== EVENT DETAIL ===================
const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('Etkinlik Detayı');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [applying, setApplying] = useState(false);
  const [apps, setApps] = useState([]);
  const [showApps, setShowApps] = useState(false);
  const [statusDialog, setStatusDialog] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const isOwner = event?.created_by === user?.id;
  const isAdmin = ['admin', 'genel_sekreter'].includes(user?.role);
  const canManage = isOwner || isAdmin;

  useEffect(() => {
    (async () => {
      try { const r = await eventsAPI.getById(id); setEvent(r.data); }
      catch { toast.error('Etkinlik bulunamadı'); navigate('/panel/etkinlikler'); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  useEffect(() => {
    if (id) viewsAPI.track('event', id).catch(() => {});
  }, [id]);

  const loadApps = async () => {
    try { const r = await eventsAPI.getApplications(id); setApps(r.data); setShowApps(true); }
    catch { toast.error('Başvurular yüklenemedi'); }
  };

  const startEdit = () => {
    setEditForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      time: event.time || '',
      end_date: event.end_date || '',
      location: event.location || '',
      quota: event.quota || '',
      meclis_name: event.meclis_name || '',
      terms: event.terms || '',
      custom_fields: event.custom_fields || [],
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...editForm, quota: editForm.quota ? parseInt(editForm.quota) : null };
      await eventsAPI.update(id, payload);
      const r = await eventsAPI.getById(id);
      setEvent(r.data);
      setEditing(false);
      toast.success('Etkinlik güncellendi');
    } catch (err) { toast.error(err.response?.data?.detail || 'Güncelleme başarısız'); }
    finally { setSaving(false); }
  };

  const handleApply = async () => {
    const fields = event.custom_fields || [];
    for (const f of fields) {
      if (f.required && !answers[f.id]) { toast.error(`"${f.label}" alanı zorunludur`); return; }
    }
    if (event.terms && !termsAccepted) { toast.error('Etkinlik şartlarını kabul etmeniz gerekmektedir'); return; }
    setApplying(true);
    try {
      await eventsAPI.apply(id, { answers, terms_accepted: termsAccepted });
      toast.success('Başvurunuz başarıyla alındı!');
      const r = await eventsAPI.getById(id); setEvent(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Başvuru yapılamadı'); }
    finally { setApplying(false); }
  };

  const handleAppStatus = async (appId, status, reason) => {
    try {
      await eventsAPI.updateAppStatus(appId, { status, rejection_reason: reason || null });
      toast.success(status === 'onaylandi' ? 'Başvuru onaylandı' : 'Başvuru reddedildi');
      const r = await eventsAPI.getApplications(id); setApps(r.data);
      const ev = await eventsAPI.getById(id); setEvent(ev.data);
    } catch { toast.error('İşlem başarısız'); }
    setStatusDialog(null);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;
  if (!event) return null;

  const st = STATUS_MAP[event.status] || STATUS_MAP.aktif;
  const myApp = event.my_application;
  const fields = event.custom_fields || [];
  const isFull = event.quota && event.application_count >= event.quota;
  const progress = event.quota ? Math.min((event.application_count / event.quota) * 100, 100) : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="event-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/panel/etkinlikler')} className="rounded-xl"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-slate-900">{event.title}</h1>
          {event.meclis_name && <p className="text-sm text-slate-500 mt-0.5">Düzenleyen: {event.meclis_name}</p>}
        </div>
        <Badge className={`${st.color} border text-xs font-medium`}>
          <span className={`w-1.5 h-1.5 rounded-full ${st.dot} mr-1.5`} />
          {st.label}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          {/* Info Card */}
          <Card className="shadow-sm rounded-2xl border-slate-200 overflow-hidden">
            <div className={`h-1.5 ${event.status === 'aktif' ? 'bg-gradient-to-r from-blue-500 to-emerald-500' : 'bg-slate-200'}`} />
            <CardContent className="p-6">
              {event.description && <p className="text-slate-600 whitespace-pre-wrap mb-6 leading-relaxed">{event.description}</p>}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3.5 rounded-xl bg-blue-50/70 border border-blue-100">
                  <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div><p className="text-[11px] text-blue-500 font-medium">Tarih</p><p className="text-sm font-semibold text-slate-800">{formatDate(event.date)}{event.end_date && ` - ${formatDate(event.end_date)}`}</p></div>
                </div>
                {event.time && <div className="flex items-center gap-3 p-3.5 rounded-xl bg-purple-50/70 border border-purple-100">
                  <Clock className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <div><p className="text-[11px] text-purple-500 font-medium">Saat</p><p className="text-sm font-semibold text-slate-800">{event.time}</p></div>
                </div>}
                {event.location && <div className="flex items-center gap-3 p-3.5 rounded-xl bg-emerald-50/70 border border-emerald-100">
                  <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <div><p className="text-[11px] text-emerald-500 font-medium">Konum</p><p className="text-sm font-semibold text-slate-800">{event.location}</p></div>
                </div>}
                {event.quota && <div className="flex items-center gap-3 p-3.5 rounded-xl bg-amber-50/70 border border-amber-100">
                  <Users className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-[11px] text-amber-500 font-medium">Kontenjan</p>
                    <p className="text-sm font-semibold text-slate-800">{event.application_count} / {event.quota}</p>
                    <div className="h-1 bg-amber-200 rounded-full mt-1 overflow-hidden">
                      <div className={`h-full rounded-full ${progress >= 100 ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                </div>}
              </div>
            </CardContent>
          </Card>

          {/* Application Status or Form */}
          {myApp ? (
            <Card className={`shadow-sm rounded-2xl overflow-hidden ${myApp.status === 'onaylandi' ? 'border-emerald-200' : myApp.status === 'reddedildi' ? 'border-red-200' : 'border-amber-200'}`}>
              <div className={`h-1 ${myApp.status === 'onaylandi' ? 'bg-emerald-500' : myApp.status === 'reddedildi' ? 'bg-red-500' : 'bg-amber-500'}`} />
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  {React.createElement(APP_STATUS[myApp.status]?.icon || AlertCircle, { className: `w-6 h-6 ${APP_STATUS[myApp.status]?.iconColor}` })}
                  <div>
                    <p className="font-semibold text-slate-900">Başvuru Durumunuz: <span className={APP_STATUS[myApp.status]?.iconColor}>{APP_STATUS[myApp.status]?.label}</span></p>
                    <p className="text-xs text-slate-400 mt-0.5">Başvuru tarihi: {formatDate(myApp.created_at)}</p>
                  </div>
                </div>
                {myApp.rejection_reason && <div className="mt-3 text-sm text-red-600 bg-red-50 p-3 rounded-xl border border-red-100">Red sebebi: {myApp.rejection_reason}</div>}
              </CardContent>
            </Card>
          ) : event.status === 'aktif' && !isFull ? (
            <Card className="shadow-sm rounded-2xl border-slate-200" data-testid="apply-form">
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Başvuru Formu</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {fields.length > 0 ? fields.map(f => (
                  <div key={f.id} className="space-y-2" data-testid={`form-field-${f.id}`}>
                    <Label className="text-sm font-medium">{f.label}{f.required && <span className="text-red-500 ml-1">*</span>}</Label>
                    {f.type === 'text' && <Input value={answers[f.id] || ''} onChange={e => setAnswers(p => ({ ...p, [f.id]: e.target.value }))} className="rounded-xl" />}
                    {f.type === 'textarea' && <Textarea rows={3} value={answers[f.id] || ''} onChange={e => setAnswers(p => ({ ...p, [f.id]: e.target.value }))} className="rounded-xl" />}
                    {f.type === 'select' && (
                      <Select value={answers[f.id] || ''} onValueChange={v => setAnswers(p => ({ ...p, [f.id]: v }))}>
                        <SelectTrigger className="rounded-xl"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>{(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    {f.type === 'radio' && (
                      <div className="flex flex-wrap gap-3">{(f.options || []).map(o => (
                        <label key={o} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${answers[f.id] === o ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                          <input type="radio" name={f.id} value={o} checked={answers[f.id] === o} onChange={() => setAnswers(p => ({ ...p, [f.id]: o }))} className="sr-only" />
                          <span className="text-sm">{o}</span>
                        </label>
                      ))}</div>
                    )}
                    {f.type === 'checkbox' && (
                      <label className="flex items-center gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={!!answers[f.id]} onChange={e => setAnswers(p => ({ ...p, [f.id]: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-blue-600" />
                        <span className="text-sm text-slate-600">Evet</span>
                      </label>
                    )}
                  </div>
                )) : <p className="text-sm text-slate-500 bg-slate-50 p-4 rounded-xl text-center">Bu etkinlik için ek soru bulunmuyor. Doğrudan başvurabilirsiniz.</p>}
                {event.terms && (
                  <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="terms-section">
                    <button type="button" onClick={() => setShowTerms(!showTerms)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                      <span className="text-sm font-medium text-slate-700">Etkinlik Şartları</span>
                      <span className="text-xs text-blue-600">{showTerms ? 'Gizle' : 'Oku'}</span>
                    </button>
                    {showTerms && (
                      <div className="px-4 py-3 bg-white border-t border-slate-100 max-h-48 overflow-y-auto">
                        <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{event.terms}</p>
                      </div>
                    )}
                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                      <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox">
                        <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                          className="w-5 h-5 rounded border-slate-300 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 leading-snug">
                          <span className="font-medium">Etkinlik Şartlarını okudum, anladım ve kabul ediyorum.</span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}
                <Button onClick={handleApply} disabled={applying || (event.terms && !termsAccepted)} className={`w-full gap-2 h-11 rounded-xl shadow-sm ${event.terms && !termsAccepted ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} data-testid="apply-btn">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Başvuru Yap
                </Button>
              </CardContent>
            </Card>
          ) : isFull && !myApp ? (
            <Card className="shadow-sm rounded-2xl border-red-200"><CardContent className="p-6 text-center">
              <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="font-medium text-red-600">Kontenjan dolmuştur</p>
              <p className="text-xs text-slate-400 mt-1">Bu etkinliğe artık başvuru yapılamamaktadır</p>
            </CardContent></Card>
          ) : null}
        </div>

        {/* Sidebar */}
        {canManage && (
          <div className="space-y-4">
            <Card className="shadow-sm rounded-2xl border-slate-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Yönetim</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" size="sm" className="w-full justify-start gap-2 rounded-lg text-blue-600 border-blue-200 hover:bg-blue-50" onClick={startEdit} data-testid="edit-event-btn">
                  <Edit className="w-4 h-4" /> Etkinliği Düzenle
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start gap-2 rounded-lg" onClick={loadApps} data-testid="view-apps-btn">
                  <UserCheck className="w-4 h-4" /> Başvuruları Görüntüle ({event.application_count})
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start gap-2 rounded-lg text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => { const url = `${process.env.REACT_APP_BACKEND_URL}/api/events/${id}/export`; window.open(url, '_blank'); }}
                  data-testid="export-excel-btn">
                  <Download className="w-4 h-4" /> Excel'e Aktar
                </Button>
                {event.status === 'aktif' ? (
                  <Button variant="outline" size="sm" className="w-full justify-start gap-2 rounded-lg text-amber-600 border-amber-200 hover:bg-amber-50" onClick={async () => { await eventsAPI.updateStatus(id, 'kapali'); const r = await eventsAPI.getById(id); setEvent(r.data); toast.success('Başvurular kapatıldı'); }}>
                    <XCircle className="w-4 h-4" /> Başvuruları Kapat
                  </Button>
                ) : event.status === 'kapali' ? (
                  <Button variant="outline" size="sm" className="w-full justify-start gap-2 rounded-lg text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={async () => { await eventsAPI.updateStatus(id, 'aktif'); const r = await eventsAPI.getById(id); setEvent(r.data); toast.success('Yeniden açıldı'); }}>
                    <CheckCircle className="w-4 h-4" /> Yeniden Aç
                  </Button>
                ) : null}
              </CardContent>
            </Card>
            <Card className="shadow-sm rounded-2xl border-slate-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Bilgi</CardTitle></CardHeader>
              <CardContent className="text-xs text-slate-500 space-y-1.5">
                <p>Oluşturan: <span className="text-slate-700">{event.created_by_name}</span></p>
                <p>Oluşturma: <span className="text-slate-700">{formatDate(event.created_at)}</span></p>
                <p>Soru Sayısı: <span className="text-slate-700">{fields.length}</span></p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Applications Dialog */}
      <Dialog open={showApps} onOpenChange={setShowApps}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="apps-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-blue-600" /> Başvurular ({apps.length})</DialogTitle></DialogHeader>
          {apps.length === 0 ? (
            <div className="py-12 text-center"><Ticket className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-400">Henüz başvuru yok</p></div>
          ) : (
            <div className="space-y-3">
              {apps.map(app => {
                const as = APP_STATUS[app.status] || APP_STATUS.beklemede;
                return (
                  <div key={app.id} className="p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-all" data-testid={`app-${app.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                          {app.user_name?.split(' ').map(n => n[0]).join('').slice(0,2)}
                        </div>
                        <div><p className="font-medium text-slate-900 text-sm">{app.user_name}</p><p className="text-[11px] text-slate-400">{formatDate(app.created_at)}</p></div>
                      </div>
                      <Badge className={`${as.color} border text-xs`}>{as.label}</Badge>
                    </div>
                    {Object.keys(app.answers || {}).length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-3 mb-3 space-y-1.5">
                        {fields.map(f => app.answers[f.id] !== undefined && (
                          <div key={f.id} className="text-xs"><span className="font-medium text-slate-600">{f.label}:</span> <span className="text-slate-500 ml-1">{String(app.answers[f.id])}</span></div>
                        ))}
                      </div>
                    )}
                    {app.status === 'beklemede' && (
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs rounded-lg gap-1" onClick={() => handleAppStatus(app.id, 'onaylandi')} data-testid={`approve-${app.id}`}>
                          <CheckCircle className="w-3.5 h-3.5" /> Onayla
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs rounded-lg text-red-600 border-red-200 hover:bg-red-50 gap-1" onClick={() => setStatusDialog(app)} data-testid={`reject-${app.id}`}>
                          <XCircle className="w-3.5 h-3.5" /> Reddet
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Başvuruyu Reddet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Red Sebebi (opsiyonel)</Label>
            <Textarea id="reject-reason" rows={3} placeholder="Neden reddedildiğini açıklayın..." className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)} className="rounded-lg">Vazgeç</Button>
            <Button className="bg-red-600 hover:bg-red-700 rounded-lg" onClick={() => {
              const reason = document.getElementById('reject-reason')?.value || '';
              handleAppStatus(statusDialog.id, 'reddedildi', reason);
            }}>Reddet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="edit-event-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-blue-600" /> Etkinliği Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Açıklama</Label>
              <Textarea rows={3} value={editForm.description || ''} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} placeholder="Etkinlik açıklaması..." className="rounded-xl" data-testid="edit-desc" /></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Tarih</Label>
                <Input type="date" value={editForm.date || ''} onChange={e => setEditForm(p => ({ ...p, date: e.target.value }))} className="rounded-xl" data-testid="edit-date" /></div>
              <div className="space-y-2"><Label>Saat</Label>
                <Input type="time" value={editForm.time || ''} onChange={e => setEditForm(p => ({ ...p, time: e.target.value }))} className="rounded-xl" data-testid="edit-time" /></div>
              <div className="space-y-2"><Label>Bitiş Tarihi</Label>
                <Input type="date" value={editForm.end_date || ''} onChange={e => setEditForm(p => ({ ...p, end_date: e.target.value }))} className="rounded-xl" data-testid="edit-end-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Konum</Label>
                <Input value={editForm.location || ''} onChange={e => setEditForm(p => ({ ...p, location: e.target.value }))} placeholder="Etkinlik yeri" className="rounded-xl" data-testid="edit-location" /></div>
              <div className="space-y-2"><Label>Kontenjan</Label>
                <Input type="number" value={editForm.quota || ''} onChange={e => setEditForm(p => ({ ...p, quota: e.target.value }))} placeholder="Sınırsız" className="rounded-xl" data-testid="edit-quota" /></div>
            </div>
            <div className="space-y-2"><Label>Etkinlik Şartları</Label>
              <Textarea rows={4} value={editForm.terms || ''} onChange={e => setEditForm(p => ({ ...p, terms: e.target.value }))} placeholder="Başvuru şartları..." className="rounded-xl" data-testid="edit-terms" />
              <p className="text-xs text-slate-400">Boş bırakılırsa başvuru sırasında onay kutusu gösterilmez.</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)} className="rounded-lg">Vazgeç</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 rounded-lg gap-2" onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { EventsListPage, NewEventPage, EventDetailPage };
